import { useApp } from "@/contexts/AppContext";
import Topbar from "@/components/layout/Topbar";
import KpiCard from "@/components/KpiCard";
import RiskBadge from "@/components/RiskBadge";
import { groupSum, groupCount } from "@/lib/forecast";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const COLORS = ["#0F172A", "#0EA5E9", "#F59E0B", "#10B981", "#EF4444", "#8B5CF6", "#64748B"];
const STATUS_COLORS = {
  "Healthy": "#10B981",
  "Stockout Risk": "#EF4444",
  "Overstock": "#0EA5E9",
  "Slow Moving": "#94A3B8",
};

// Hoisted chart config — defined once so Recharts doesn't see new prop refs each render.
const CHART_MARGIN = { top: 8, right: 8, bottom: 0, left: 0 };
const CHART_MARGIN_HORIZ = { top: 8, right: 16, bottom: 0, left: 16 };
const PIE_MARGIN = CHART_MARGIN;
const AXIS_TICK = { fontSize: 11, fill: "#64748B" };
const AXIS_LINE = { stroke: "#E2E8F0" };
const TOOLTIP_CURSOR = { fill: "#F8FAFC" };
const BAR_RADIUS_TOP = [3, 3, 0, 0];
const BAR_RADIUS_RIGHT = [0, 3, 3, 0];
const GRID_DASH = "2 2";
const GRID_STROKE = "#F1F5F9";
const LEGEND_STYLE = { fontSize: 11 };

const fmt = (n) => {
  if (!Number.isFinite(n)) return "0";
  if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (Math.abs(n) >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return String(Math.round(n));
};

export default function Dashboard() {
  const { forecast, settings } = useApp();
  const { rows, kpi } = forecast;
  const cur = settings.currency;

  const demandByCategory = groupSum(rows, "Category", "ForecastDemand")
    .sort((a, b) => b.value - a.value);

  const stockoutBySite = groupCount(rows, "Site", (r) => r.StockoutRisk !== "Low");

  const statusData = [
    { name: "Healthy", value: kpi.healthyItems },
    { name: "Stockout Risk", value: kpi.stockoutRiskItems },
    { name: "Overstock", value: kpi.overstockItems },
    { name: "Slow Moving", value: kpi.slowMovingItems },
  ];

  const topReorder = [...rows]
    .filter((r) => r.RecommendedOrderQty > 0)
    .sort((a, b) => b.ReorderValue - a.ReorderValue)
    .slice(0, 10);

  const topOverstock = [...rows]
    .filter((r) => r.OverstockQty > 0)
    .sort((a, b) => b.OverstockValue - a.OverstockValue)
    .slice(0, 10);

  return (
    <>
      <Topbar
        title="Executive Dashboard"
        subtitle="Forecast MRO consumables before they disrupt operations."
      />
      <div className="flex-1 px-8 py-7 space-y-6">
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 text-slate-200 text-sm leading-relaxed">
          <span className="font-heading font-semibold text-white">Avyntis</span> helps MRO and procurement teams forecast consumables demand, prevent stockouts, reduce overstock, and improve supplier planning — without replacing ERP or operating a marketplace.
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          <KpiCard testid="kpi-skus" label="SKUs Analyzed" value={fmt(kpi.totalSKUs)} />
          <KpiCard testid="kpi-forecast" label="Forecast Demand" value={fmt(kpi.totalForecastDemand)} sub={`Next ${settings.forecastHorizon} days (base units)`} />
          <KpiCard testid="kpi-stockout" label="Stockout Risk" value={fmt(kpi.stockoutRiskItems)} sub="High + Medium" tone="danger" />
          <KpiCard testid="kpi-overstock" label="Overstock Items" value={fmt(kpi.overstockItems)} sub={`${cur}${fmt(kpi.overstockValue)} excess`} tone="info" />
          <KpiCard testid="kpi-reorder-value" label="Reorder Value" value={`${cur}${fmt(kpi.reorderValue)}`} sub="Recommended PO spend" />
          <KpiCard testid="kpi-capital-risk" label="Working Capital At Risk" value={`${cur}${fmt(kpi.workingCapitalAtRisk)}`} sub="Reorder + overstock" tone="warn" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white border border-slate-200 rounded-lg p-5 lg:col-span-2" data-testid="chart-demand-by-category">
            <div className="flex items-baseline justify-between mb-4">
              <h3 className="font-heading font-semibold text-slate-900">Forecast Demand by Category</h3>
              <span className="text-xs text-slate-500">Base units</span>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={demandByCategory} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray={GRID_DASH} stroke={GRID_STROKE} vertical={false} />
                  <XAxis dataKey="name" tick={AXIS_TICK} axisLine={AXIS_LINE} tickLine={false} />
                  <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} />
                  <Tooltip cursor={TOOLTIP_CURSOR} />
                  <Bar dataKey="value" fill="#0F172A" radius={BAR_RADIUS_TOP} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="chart-inventory-status">
            <h3 className="font-heading font-semibold text-slate-900 mb-4">Inventory Status</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {statusData.map((e, i) => (
                      <Cell key={e.name} fill={STATUS_COLORS[e.name] || COLORS[i]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend iconType="circle" wrapperStyle={LEGEND_STYLE} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="chart-stockout-by-site">
            <h3 className="font-heading font-semibold text-slate-900 mb-4">Stockout Risk by Site</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stockoutBySite} layout="vertical" margin={CHART_MARGIN_HORIZ}>
                  <CartesianGrid strokeDasharray={GRID_DASH} stroke={GRID_STROKE} horizontal={false} />
                  <XAxis type="number" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                  <YAxis dataKey="name" type="category" tick={AXIS_TICK} axisLine={false} tickLine={false} width={80} />
                  <Tooltip cursor={TOOLTIP_CURSOR} />
                  <Bar dataKey="value" fill="#EF4444" radius={BAR_RADIUS_RIGHT} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg lg:col-span-2" data-testid="top-reorder">
            <div className="px-5 py-4 border-b border-slate-200 flex items-baseline justify-between">
              <h3 className="font-heading font-semibold text-slate-900">Top 10 Reorder Recommendations</h3>
              <span className="text-xs text-slate-500">By reorder value</span>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Item</th>
                    <th>Site</th>
                    <th className="num">Reorder Qty</th>
                    <th className="num">Value</th>
                    <th>Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {topReorder.map((r) => (
                    <tr key={r.SKU + r.Site}>
                      <td className="font-mono text-slate-900">{r.SKU}</td>
                      <td className="text-slate-700">{r.ItemName}</td>
                      <td className="text-slate-500">{r.Site}</td>
                      <td className="num">{fmt(r.RecommendedOrderQty)}</td>
                      <td className="num">{cur}{fmt(r.ReorderValue)}</td>
                      <td><RiskBadge level={r.StockoutRisk} /></td>
                    </tr>
                  ))}
                  {topReorder.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-sm text-slate-500 py-8">No reorder recommendations.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg" data-testid="top-overstock">
          <div className="px-5 py-4 border-b border-slate-200 flex items-baseline justify-between">
            <h3 className="font-heading font-semibold text-slate-900">Top 10 Excess Inventory Items</h3>
            <span className="text-xs text-slate-500">By overstock value</span>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Item</th>
                  <th>Site</th>
                  <th>Category</th>
                  <th className="num">Current</th>
                  <th className="num">Max</th>
                  <th className="num">Excess Qty</th>
                  <th className="num">Excess Value</th>
                </tr>
              </thead>
              <tbody>
                {topOverstock.map((r) => (
                  <tr key={r.SKU + r.Site}>
                    <td className="font-mono text-slate-900">{r.SKU}</td>
                    <td className="text-slate-700">{r.ItemName}</td>
                    <td className="text-slate-500">{r.Site}</td>
                    <td className="text-slate-500">{r.Category}</td>
                    <td className="num">{fmt(r.CurrentStock)}</td>
                    <td className="num">{fmt(r.MaxStock)}</td>
                    <td className="num">{fmt(r.OverstockQty)}</td>
                    <td className="num">{cur}{fmt(r.OverstockValue)}</td>
                  </tr>
                ))}
                {topOverstock.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-sm text-slate-500 py-8">No overstock items.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
